import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { RecordPreviewProps } from '@all-manager/design-system-v2';
import type { ExistingMatch, ExistingMatchKind, ExistingRecord } from '../types/inbox.types';

type ResourceColorName = RecordPreviewProps['color'];

type PreviewLink = {
  label: string;
  path: string;
};

export type ExistingMatchPreview = {
  color: ResourceColorName;
  heading: string;
  title: string;
  details: string[];
  isCompleted: boolean;
  /** Set when the record has been deleted since the e-mail was analyzed. */
  missingNotice: string | null;
  /** Path inside the space, to be prefixed with the space slug. */
  link: PreviewLink | null;
};

const KIND_PREVIEW: Record<ExistingMatchKind, { color: ResourceColorName; heading: string }> = {
  event: { color: 'rendezVous', heading: 'Rendez-vous similaire déjà enregistré' },
  pending_item: { color: 'pending_item', heading: 'Élément à suivre similaire déjà ouvert' },
  task: { color: 'task', heading: 'Tâche similaire déjà ouverte' },
  contact: { color: 'providers', heading: 'Contact déjà enregistré' },
};

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatRecordDate(date: string, allDay?: boolean | null): string {
  const parsed = new Date(date);
  const withoutTime = allDay || format(parsed, 'HH:mm') === '00:00';
  const pattern = withoutTime ? 'EEEE d MMMM yyyy' : "EEEE d MMMM yyyy 'à' HH'h'mm";
  return capitalize(format(parsed, pattern, { locale: fr }));
}

function getDetails(kind: ExistingMatchKind, record: ExistingRecord): string[] {
  if (kind === 'contact') {
    const channels = [record.email, record.phone].filter(Boolean).join(' · ');
    return [record.providerName, record.role, channels].filter((value): value is string => Boolean(value));
  }

  return [
    record.date ? formatRecordDate(record.date, record.allDay) : null,
    record.providerName ? `Prestataire : ${record.providerName}` : null,
    record.subTopicName ? `Sujet : ${record.subTopicName}` : null,
  ].filter((value): value is string => Boolean(value));
}

function getLink(kind: ExistingMatchKind, record: ExistingRecord): PreviewLink | null {
  if (kind === 'contact') {
    return record.providerId ? { label: 'Voir le prestataire', path: `/providers/${record.providerId}` } : null;
  }

  return record.subTopicId ? { label: 'Ouvrir le sujet', path: `/topics/${record.subTopicId}` } : null;
}

/**
 * What to show of the record a suggestion may duplicate. Before the record is loaded (or once it
 * is gone) the label and date saved with the match stand in for it.
 */
export function buildExistingMatchPreview(
  match: ExistingMatch,
  record: ExistingRecord | null | undefined
): ExistingMatchPreview {
  const kindPreview = KIND_PREVIEW[match.kind];
  const fallback: ExistingRecord = { title: match.label, date: match.date };
  const current = record ?? fallback;

  return {
    color: kindPreview.color,
    heading: kindPreview.heading,
    title: current.title || match.label,
    details: getDetails(match.kind, current),
    isCompleted: Boolean(record?.isCompleted),
    missingNotice: record === null ? "Cet élément n'existe plus : il a sans doute été supprimé depuis l'analyse." : null,
    link: record ? getLink(match.kind, record) : null,
  };
}
